// src/components/GettingStartedNavigator.tsx

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Question, UserAnswer, ImplementationPlan } from '../types';
import { gettingStartedQuestions } from '../data/gettingStartedQuestions';
import { getVisibleQuestions, getNextQuestion, getProgressPercentage } from '../engine/branching';
import { generateImplementationPlan } from '../engine/implementationPlanGenerator';
import QuestionCard from './QuestionCard';
import ProgressBar from './ProgressBar';
import ImplementationPlanResults from './ImplementationPlanResults';
import EmailCapture from './EmailCapture';
import NavigatorNav from './NavigatorNav';
import { InfoPanel } from './InfoPanel';
import { LegalDisclaimer } from './LegalDisclaimer';

interface GettingStartedNavigatorProps {
  onBackToModes?: () => void;
}

const GettingStartedNavigator: React.FC<GettingStartedNavigatorProps> = ({ onBackToModes }) => {
  const [answers, setAnswers] = useState<UserAnswer[]>([]);
  const [visibleQuestions, setVisibleQuestions] = useState<Question[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
  const [implementationPlan, setImplementationPlan] = useState<ImplementationPlan | null>(null);
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  // Update visible questions whenever answers change
  useEffect(() => {
    const visible = getVisibleQuestions(gettingStartedQuestions, answers);
    setVisibleQuestions(visible);

    const answeredIds = answers.map(a => a.questionId);
    const next = getNextQuestion(visible, answeredIds);

    if (next) {
      setCurrentQuestion(next);
    } else {
      // All questions answered - generate implementation plan
      const plan = generateImplementationPlan(answers);
      setImplementationPlan(plan);
      setShowEmailCapture(true);
    }
  }, [answers]);

  const handleAnswer = (questionId: string, value: string | string[]) => {
    // Update or add answer
    setAnswers(prev => {
      const existingIndex = prev.findIndex(a => a.questionId === questionId);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = { questionId, value };
        return updated;
      }
      return [...prev, { questionId, value }];
    });
  };

  const handleBack = () => {
    if (answers.length === 0) return;

    // Remove last answer
    setAnswers(prev => prev.slice(0, -1));
  };

  const handleEmailSubmit = (email: string) => {
    setUserEmail(email);
    setShowEmailCapture(false);
    // TODO: Send to ConvertKit with tag "Navigator_GettingStarted_Complete"
  };

  const handleRestart = () => {
    setAnswers([]);
    setImplementationPlan(null);
    setShowEmailCapture(false);
    setUserEmail(null);
  };

  const progress = getProgressPercentage(answers.length, visibleQuestions.length);

  // Show disclaimer first
  if (!disclaimerAccepted) {
    return <LegalDisclaimer type="pre-assessment" onAccept={() => setDisclaimerAccepted(true)} />;
  }

  if (showEmailCapture && implementationPlan) {
    return (
      <EmailCapture
        mode="gettingStarted"
        result={null}
        implementationPlan={implementationPlan}
        onSubmit={handleEmailSubmit}
        onSkip={() => setShowEmailCapture(false)}
      />
    );
  }

  if (implementationPlan && userEmail) {
    return (
      <ImplementationPlanResults
        plan={implementationPlan}
        userEmail={userEmail}
        onRestart={handleRestart}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <NavigatorNav />
      <div className="py-8 px-4">
        <div className={`mx-auto transition-all duration-300 ${isInfoOpen ? 'max-w-7xl' : 'max-w-4xl'}`}>
          <div className="flex gap-6">
            {/* Main Content */}
            <div className={`transition-all duration-300 ${isInfoOpen ? 'flex-1' : 'w-full'}`}>
              {/* Header */}
              <div className="text-center mb-8">
                <div className="inline-block bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-bold mb-4">
                  🚀 GETTING STARTED MODE
                </div>
                <h1 className="text-4xl font-bold text-gray-900 mb-2">
                Your HIPAA Implementation Plan
              </h1>
              <p className="text-lg text-gray-600">
                Answer questions about your planned architecture to receive a customized 4-month roadmap
              </p>
              </div>

              {/* Progress Bar */}
              <ProgressBar
                current={answers.length}
                total={visibleQuestions.length}
                percentage={progress}
              />

              {/* Question Card */}
              <AnimatePresence mode="wait">
                {currentQuestion && (
                  <motion.div
                    key={currentQuestion.id}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.3 }}
                  >
                    <QuestionCard
                      question={currentQuestion}
                      onAnswer={handleAnswer}
                      onBack={handleBack}
                      canGoBack={answers.length > 0}
                      currentIndex={answers.length + 1}
                      totalQuestions={visibleQuestions.length}
                      isInfoOpen={isInfoOpen}
                      setIsInfoOpen={setIsInfoOpen}
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Footer */}
              <div className="mt-8 text-center text-sm text-gray-500">
                <p>
                  🔒 Your responses are used only to generate your personalized 4-month implementation plan
                </p>
                <p className="mt-2">
                  Based on NIST SP 800-66r2 and official HIPAA regulations
                </p>
              </div>
            </div>

            {/* Tooltip Panel - Side by side on desktop */}
            {isInfoOpen && currentQuestion && (
              <div className="hidden lg:block flex-shrink-0 w-96">
                <div className="sticky top-8">
                  <InfoPanel
                    question={currentQuestion}
                    isOpen={isInfoOpen}
                    onClose={() => setIsInfoOpen(false)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GettingStartedNavigator;
